import type {
	BufferedSegmentRef,
	LocalBufferPort,
	LocalStorageEstimate,
} from '@Domain/Ports/LocalBuffer/LocalBufferPort';
import openDeviceDatabase, { CHUNKS_STORE_NAME } from '@Infra/Device/openDeviceDatabase';
import waitForIndexedDbRequest from '@Infra/Device/waitForIndexedDbRequest';
import waitForIndexedDbTransaction from '@Infra/Device/waitForIndexedDbTransaction';

/** Injected time source: lets `writtenAt` be asserted exactly in tests instead of racing the wall clock. */
type Clock = {
	now(): number;
};

/** The row shape actually written to the `chunks` store — opaque bytes plus their own timestamp. */
type ChunkRecord = {
	readonly itemId: string;
	readonly segment: number;
	readonly index: number;
	readonly blob: Blob;
	readonly byteSize: number;
	readonly writtenAt: number;
};

type SegmentAggregate = {
	readonly chunkCount: number;
	readonly byteSize: number;
	readonly lastWriteAt: number;
};

const OPERATION_ERROR_MESSAGE = "Échec de l'opération sur le tampon local.";
const TRANSACTION_ERROR_MESSAGE = 'Échec de la transaction sur le tampon local.';
const TRANSACTION_ABORT_MESSAGE = 'Transaction sur le tampon local interrompue.';

/**
 * Device-local transfer buffer backed by IndexedDB, storing capture bytes as `Blob`s.
 *
 * Every browser surface is received through the constructor, defaulted from the real globals only
 * at construction time: no polyfill is ever assumed to exist on `globalThis`. `indexedDbFactory` is
 * typed `IDBFactory | undefined` — the lib DOM declares `globalThis.indexedDB` always present, but
 * jsdom and older browsers simply omit it, so the type must not claim a guarantee the default
 * cannot give. The database itself is opened lazily, on the first call that genuinely needs it —
 * never here, in the constructor — so that building this adapter never deposits a database on the
 * device by itself, and a missing factory only fails there, with a French message naming the cause.
 */
class IndexedDbLocalBufferAdapter implements LocalBufferPort {
	private readonly indexedDbFactory: IDBFactory | undefined;
	private readonly storageManager: StorageManager | undefined;
	private readonly clock: Clock;
	private databasePromise: Promise<IDBDatabase> | null;

	constructor(
		indexedDbFactory: IDBFactory | undefined = globalThis.indexedDB,
		storageManager: StorageManager | undefined = globalThis.navigator?.storage,
		clock: Clock = { now: () => Date.now() },
	) {
		this.indexedDbFactory = indexedDbFactory;
		this.storageManager = storageManager;
		this.clock = clock;
		this.databasePromise = null;
	}

	isSupported(): boolean {
		return Boolean(this.indexedDbFactory);
	}

	async appendChunk(itemId: string, segment: number, index: number, chunk: Blob): Promise<void> {
		const database = await this.openDatabase();
		const record: ChunkRecord = {
			itemId,
			segment,
			index,
			blob: chunk,
			byteSize: chunk.size,
			writtenAt: this.clock.now(),
		};

		const transaction = database.transaction(CHUNKS_STORE_NAME, 'readwrite');
		transaction.objectStore(CHUNKS_STORE_NAME).put(record);
		await waitForIndexedDbTransaction(transaction, TRANSACTION_ERROR_MESSAGE, TRANSACTION_ABORT_MESSAGE);
	}

	async listSegments(itemId: string): Promise<readonly BufferedSegmentRef[]> {
		const records = await this.readChunksByItem(itemId);
		const aggregatesBySegment = new Map<number, SegmentAggregate>();

		for (const record of records) {
			const existingAggregate = aggregatesBySegment.get(record.segment);

			if (existingAggregate === undefined) {
				aggregatesBySegment.set(record.segment, {
					chunkCount: 1,
					byteSize: record.byteSize,
					lastWriteAt: record.writtenAt,
				});
			} else {
				aggregatesBySegment.set(record.segment, {
					chunkCount: existingAggregate.chunkCount + 1,
					byteSize: existingAggregate.byteSize + record.byteSize,
					lastWriteAt: Math.max(existingAggregate.lastWriteAt, record.writtenAt),
				});
			}
		}

		return Array.from(aggregatesBySegment.entries())
			.sort(([firstSegment], [secondSegment]) => firstSegment - secondSegment)
			.map(([segment, aggregate]) => ({ segment, ...aggregate }));
	}

	/**
	 * Assembles the one segment asked for. There is deliberately no method that joins two segments.
	 *
	 * Fragments are recombined from their raw bytes (`arrayBuffer()`), never by handing the stored
	 * `Blob` objects straight to the `Blob` constructor: a `Blob` instance built by one global realm
	 * is not always recognised as a blob part by another realm's `Blob` constructor, which would
	 * silently stringify it instead of reading its bytes. Reading bytes first sidesteps that
	 * altogether, in every realm.
	 */
	async assembleSegment(itemId: string, segment: number, mimeType: string): Promise<Blob> {
		const database = await this.openDatabase();
		const transaction = database.transaction(CHUNKS_STORE_NAME, 'readonly');
		const request = transaction
			.objectStore(CHUNKS_STORE_NAME)
			.index('byItemSegment')
			.getAll([itemId, segment]);
		const records: ChunkRecord[] = await waitForIndexedDbRequest(request, OPERATION_ERROR_MESSAGE);
		const fragmentBuffers = await Promise.all(records.map((record) => record.blob.arrayBuffer()));

		return new Blob(fragmentBuffers, { type: mimeType });
	}

	async deleteItem(itemId: string): Promise<void> {
		const database = await this.openDatabase();
		const transaction = database.transaction(CHUNKS_STORE_NAME, 'readwrite');
		const cursorRequest = transaction.objectStore(CHUNKS_STORE_NAME).index('byItem').openCursor(itemId);

		cursorRequest.onsuccess = () => {
			const cursor = cursorRequest.result;

			if (cursor !== null) {
				cursor.delete();
				cursor.continue();
			}
		};

		await waitForIndexedDbTransaction(transaction, TRANSACTION_ERROR_MESSAGE, TRANSACTION_ABORT_MESSAGE);
	}

	async totalBytes(): Promise<number> {
		const database = await this.openDatabase();
		const transaction = database.transaction(CHUNKS_STORE_NAME, 'readonly');
		const request = transaction.objectStore(CHUNKS_STORE_NAME).getAll();
		const records: ChunkRecord[] = await waitForIndexedDbRequest(request, OPERATION_ERROR_MESSAGE);

		return records.reduce((total, record) => total + record.byteSize, 0);
	}

	async estimate(): Promise<LocalStorageEstimate> {
		if (this.storageManager === undefined) {
			return { usageBytes: null, quotaBytes: null };
		}

		const estimate = await this.storageManager.estimate();

		return {
			usageBytes: estimate.usage ?? null,
			quotaBytes: estimate.quota ?? null,
		};
	}

	async requestPersistence(): Promise<boolean> {
		if (this.storageManager === undefined) {
			return false;
		}

		return this.storageManager.persist();
	}

	private openDatabase(): Promise<IDBDatabase> {
		if (this.indexedDbFactory === undefined) {
			return Promise.reject(new Error("IndexedDB n'est pas disponible sur cet appareil."));
		}

		if (this.databasePromise === null) {
			this.databasePromise = openDeviceDatabase(this.indexedDbFactory);
		}

		return this.databasePromise;
	}

	private async readChunksByItem(itemId: string): Promise<ChunkRecord[]> {
		const database = await this.openDatabase();
		const transaction = database.transaction(CHUNKS_STORE_NAME, 'readonly');
		const request = transaction.objectStore(CHUNKS_STORE_NAME).index('byItem').getAll(itemId);

		return waitForIndexedDbRequest(request, OPERATION_ERROR_MESSAGE);
	}
}

export default IndexedDbLocalBufferAdapter;
